import { OrderReferenceType } from 'data/ppoints/order-reference-types';
import { ORDER_SEND_MODE_TYPE } from 'data/ppoints/send-order-mode';
import { PackageProviderName, SettingKey } from '../../../data/ppoints/constants';
import { GlsExtraPackageServices } from '../../../data/ppoints/enums/GlsExtraPackageServices';
import { ShippingType } from '../../../features/orders/models/ShippingType';
import { HomeDeliverySetting } from './HomeDeliverySetting';
import {
    DpdLabelTypeSetting,
    FoxpostLabelTypeSetting,
    FoxpostPacketaLabelTypeSetting,
    GlsLabelTypeSetting,
    MplLabelTypeSetting,
    PacketaLabelTypeSetting,
    SprinterLabelTypeSetting,
} from './LabelTypes';
import { RatesToPaymentMethodsSetting } from './RatesToPaymentMethodsSetting';
import { ShippingMethodsSetting } from './ShippingMethodsSetting';

export type DefaultLanguageSetting = string;
export type FoxpostApiKeysSetting = { api_key: string; password: string; username: string };
export type MplApiKeysSetting = {
    api_key: string;
    accounting_code: string;
    agreement_code: string;
};
export interface MplApiKeySettingsFormProps extends MplApiKeysSetting {
    is_live?: boolean;
    api_secret: string;
    third_party_fulfillment: boolean;
}

export interface FoxpostApiKeysSettingFormProps extends FoxpostApiKeysSetting {
    is_live?: boolean;
    third_party_fulfillment: boolean;
}
export type GlsApiKeysSetting = {
    password: string;
    username: string;
    client_id: number;
    is_live: boolean;
    country?: string;
};
export type PacketaApiKeysSetting = { api_key: string; api_password: string };
export type DpdApiKeysSetting = { password: string; username: string };
export type ZonesAndProvidersSetting = {
    [key: string]: {
        [key in PackageProviderName]?: {
            enabled: boolean;
            shipping_rates: {
                id: number;
                name: string;
                price: string;
            }[];
        };
    };
};

export type TourmixApiKeysSetting = { api_token: string; is_live: boolean };

export type SelectedLanguages = string[];

export interface GlsApiKeysSettingFormProps extends GlsApiKeysSetting {
    third_party_fulfillment: boolean;
}

export interface PacketaApiKeysSettingFromProps extends PacketaApiKeysSetting {
    is_live?: boolean;
    third_party_fulfillment: boolean;
}

export interface DpdApiKeysSettingFromProps extends DpdApiKeysSetting {
    third_party_fulfillment: boolean;
}

export interface TourmixApiKeysSettingFromProps extends TourmixApiKeysSetting {
    third_party_fulfillment: boolean;
}

export type CashOnDeliveryForProvider = {
    cod: boolean;
    selected_payment_gateways: string[];
};

export type PaymentMethodForProvider = {
    selected_shipping_rates: {
        id: number;
        name: string;
        price: string;
    };
    selected_payment_gateways: string[];
};

export type CashOnDeliverySetting = {
    [PackageProviderName.FOXPOST]: CashOnDeliveryForProvider;
    [PackageProviderName.MPL_PACKAGE_POINT]: CashOnDeliveryForProvider;
    [PackageProviderName.GLS]: CashOnDeliveryForProvider;
    [PackageProviderName.DPD]: CashOnDeliveryForProvider;
    [PackageProviderName.PACKETA]: CashOnDeliveryForProvider;
    [PackageProviderName.SPRINTER]: CashOnDeliveryForProvider;
};

export type PaymentMehodSetting = {
    [PackageProviderName.FOXPOST]: PaymentMethodForProvider;
    [PackageProviderName.MPL_PACKAGE_POINT]: PaymentMethodForProvider;
    [PackageProviderName.GLS]: PaymentMethodForProvider;
    [PackageProviderName.DPD]: PaymentMethodForProvider;
    [PackageProviderName.PACKETA]: PaymentMethodForProvider;
    [PackageProviderName.SPRINTER]: PaymentMethodForProvider;
};

export type PaymentMappingType = PackageProviderName | 'hd';

export interface PaymentMappingInput {
    type: ShippingType;
    payment_methods: string[];
}

export interface MappedPaymentMethods {
    [PackageProviderName.MPL_PACKAGE_POINT]: PaymentMappingInput[];
    [PackageProviderName.GLS]: PaymentMappingInput[];
    [PackageProviderName.FOXPOST]: PaymentMappingInput[];
}

export const MPL_PACKAGE_SIZES = ['S', 'M', 'L', 'PRINT', 'PACK'];

export const FOXPOST_PACKAGE_SIZES = ['XS', 'S', 'M', 'L', 'XL'];

export type MplPackageSizeSetting = (typeof MPL_PACKAGE_SIZES)[number];

export type FoxpostPackageSizeSetting = (typeof FOXPOST_PACKAGE_SIZES)[number];

export type MplPackageWeightSetting = number;

export type DpdPackageWeightSetting = number;

export type SenderDetailsSetting = {
    company_name: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    country_code: string;
    zipcode: string;
    city: string;
    street: string;
    street_type: string;
    house_number: string;
    building: string;
    floor: string;
    apartment_number: string;
};
export type SenderDetailsExtraMplSetting = {
    accountNo: string;
};

export type MapDesignSettings = {
    pin_type: PinType | undefined;
    map_type: MapType | undefined;
    button: ButtonType | undefined;
    font: string;
    title: string;
    rounded: string;
    subtitle: string;
    rounded_px: string;
    button_text: string;
    primary_color: string;
    secondary_color: string;
    button_text_color: string;
};

//TODO: Change to use only string keys
export type ProviderToCountrySetting = {
    [key: string]: PackageProviderName[];
};

export type CartButtonSetting = {
    button: ButtonType | undefined;
    layout: LayoutType | undefined;
    buttonTextColor: string | undefined;
    textColor: string | undefined;
    buttonColor: string | undefined;
    backgroundColor: string | undefined;
    // highlightColor: string;
    strokeColor: string | undefined;
    // new
    v4InactiveBackgroundColor: string | undefined;
    v4ActiveBackgroundColor: string | undefined;
    v4InactiveIconColor: string | undefined;
    v4ActiveIconColor: string | undefined;
    v4InactiveStrokeColor: string | undefined;
    v4ActiveStrokeColor: string | undefined;
    allowPickup: boolean;
};

export type CheckoutMapIntegrationSettings = {
    map_checkout_design: CheckoutMapSetting;
    button_design: CheckoutMapButtonSetting;
    google_api_map_key: string;
};
export type CheckoutMapButtonSetting = {
    layout: LayoutType;
    allowPickup: boolean;
};
export type CheckoutMapSetting = {
    pinType: PinType;
};

export type CheckoutSettings = {
    package_points_num: number;
    display_package_point_id: boolean;
};

export enum IntegrationModeSetting {
    map = 'map',
    checkout = 'checkout',
    checkout_map = 'checkout_map',
    thank_you_page_map = 'thank_you_page_map',
    smart_delivery_page = 'smart_delivery_page',
}

export type EnabledShippingTypesSetting = {
    [key: string]: {
        [key in PackageProviderName]: ShippingType[];
    };
};

export type AppActivatedSetting = boolean;

export const BUTTON_TYPES = ['rectangular', 'normal', 'circular'];

export const LAYOUT_TYPES = ['space-between', 'center', 'no-text', 'select-delivery-type', 'choice-list-type'];

export const MAP_TYPES = ['color', 'gray'];

export const PIN_TYPES = ['normal', 'round', 'curved'];

export type ButtonType = (typeof BUTTON_TYPES)[number];

export type LayoutType = (typeof LAYOUT_TYPES)[number];

export type MapType = (typeof MAP_TYPES)[number];

export type PinType = (typeof PIN_TYPES)[number];

export type SettingsToEcom = {
    [SettingKey.default_language]?: DefaultLanguageSetting;
    //API keys
    [SettingKey.foxpost_api_keys]?: FoxpostApiKeysSetting;
    [SettingKey.mpl_api_keys]?: MplApiKeysSetting;
    [SettingKey.dpd_api_keys]?: DpdApiKeysSetting;
    [SettingKey.gls_api_keys]?: GlsApiKeysSetting;
    [SettingKey.packeta_api_keys]?: PacketaApiKeysSetting;
    [SettingKey.tourmix_api_keys]?: TourmixApiKeysSetting;

    //Zones and providers
    [SettingKey.zones_and_providers]?: ZonesAndProvidersSetting;
    [SettingKey.shipping_methods]?: ShippingMethodsSetting;
    [SettingKey.mpl_package_size]?: MplPackageSizeSetting;
    [SettingKey.mpl_package_weight]?: MplPackageWeightSetting;
    [SettingKey.mpl_label_type]?: MplLabelTypeSetting;
    [SettingKey.foxpost_label_type]?: FoxpostLabelTypeSetting;
    [SettingKey.foxpost_packeta_label_type]?: FoxpostPacketaLabelTypeSetting;
    [SettingKey.gls_label_type]?: GlsLabelTypeSetting;
    [SettingKey.packeta_label_type]?: PacketaLabelTypeSetting;
    [SettingKey.dpd_label_type]?: DpdLabelTypeSetting;
    [SettingKey.sprinter_label_type]?: SprinterLabelTypeSetting;
    [SettingKey.sender_details]?: SenderDetailsSetting;
    [SettingKey.sender_details_extra_mpl]?: SenderDetailsExtraMplSetting;
    [SettingKey.map_design]?: MapDesignSettings;
    [SettingKey.cart_button_design]?: CartButtonSetting;
    [SettingKey.provider_to_country]?: ProviderToCountrySetting;
    [SettingKey.integration_mode]?: IntegrationModeSetting;
    [SettingKey.home_delivery]?: HomeDeliverySetting;
    [SettingKey.cash_onelivery]?: CashOnDeliverySetting;
    [SettingKey.payment_method]?: PaymentMehodSetting;
    //[SettingKey.selected_languages]?: SelectedLanguages;
    [SettingKey.enabled_shipping_types]?: EnabledShippingTypesSetting;

    [SettingKey.test_api_keys_mpl]?: MplApiKeysSetting;
    [SettingKey.test_api_keys_foxpost]?: FoxpostApiKeysSetting;
    [SettingKey.test_api_keys_gls]?: GlsApiKeysSetting;
    [SettingKey.test_api_keys_packeta]?: PacketaApiKeysSetting;

    [SettingKey.use_test_api_mpl]?: boolean;
    [SettingKey.use_test_api_foxpost]?: boolean;
    [SettingKey.use_test_api_gls]?: boolean;
    [SettingKey.use_test_api_tourmix]?: boolean;

    [SettingKey.mapped_payment_methods]?: MappedPaymentMethods;
    [SettingKey.use_test_api_packeta]?: boolean;

    [SettingKey.mpl_third_party_fulfillment]?: boolean;
    [SettingKey.gls_third_party_fulfillment]?: boolean;
    [SettingKey.packeta_third_party_fulfillment]?: boolean;
    [SettingKey.foxpost_third_party_fulfillment]?: boolean;
    [SettingKey.dpd_third_party_fulfillment]?: boolean;
    [SettingKey.tourmix_third_party_fulfillment]?: boolean;

    [SettingKey.mpl_customer_note]?: boolean;
    [SettingKey.gls_customer_note]?: boolean;
    [SettingKey.packeta_customer_note]?: boolean;
    [SettingKey.foxpost_customer_note]?: boolean;
    [SettingKey.dpd_customer_note]?: boolean;

    [SettingKey.mpl_bulk_print_pdf]?: boolean;
    [SettingKey.gls_bulk_print_pdf]?: boolean;
    [SettingKey.packeta_bulk_print_pdf]?: boolean;
    [SettingKey.foxpost_bulk_print_pdf]?: boolean;
    [SettingKey.dpd_bulk_print_pdf]?: boolean;
    [SettingKey.tourmix_bulk_print_pdf]?: boolean;

    [SettingKey.gls_order_send_mode]?: ORDER_SEND_MODE_TYPE;
    [SettingKey.mpl_order_send_mode]?: ORDER_SEND_MODE_TYPE;
    [SettingKey.mpl_delivery_time_mode]?: ORDER_SEND_MODE_TYPE;
    [SettingKey.packeta_order_send_mode]?: ORDER_SEND_MODE_TYPE;
    [SettingKey.foxpost_order_send_mode]?: ORDER_SEND_MODE_TYPE;
    [SettingKey.dpd_order_send_mode]?: ORDER_SEND_MODE_TYPE;
    [SettingKey.tourmix_order_send_mode]?: ORDER_SEND_MODE_TYPE;

    [SettingKey.dpd_sms_notification]?: boolean;
    [SettingKey.dpd_package_weight]?: DpdPackageWeightSetting;
    [SettingKey.foxpost_package_size]?: FoxpostPackageSizeSetting;
    [SettingKey.rates_to_payment_methods]?: RatesToPaymentMethodsSetting;

    [SettingKey.gls_extra_package_services]?: GlsExtraPackageServices;

    [SettingKey.mpl_package_sending_mode]?: string;

    [SettingKey.foxpost_order_number_on_label]?: boolean;

    [SettingKey.order_reference_gls]?: OrderReferenceType;

    [SettingKey.mpl_value_insurance]?: boolean;

    [SettingKey.gls_auto_fulfill]?: boolean;
    [SettingKey.tourmix_auto_fulfill]?: boolean;
    [SettingKey.mpl_auto_fulfill]?: boolean;
    [SettingKey.foxpost_auto_fulfill]?: boolean;
    [SettingKey.dpd_auto_fulfill]?: boolean;
    [SettingKey.packeta_auto_fulfill]?: boolean;
};

export type SettingType =
    | DefaultLanguageSetting
    // | SelectedLanguages
    | FoxpostApiKeysSetting
    | MplApiKeysSetting
    | GlsApiKeysSetting
    | PacketaApiKeysSetting
    | ZonesAndProvidersSetting
    | MplPackageSizeSetting
    | MplPackageWeightSetting
    | SenderDetailsSetting
    | MapDesignSettings
    | ProviderToCountrySetting
    | CartButtonSetting
    | IntegrationModeSetting
    | AppActivatedSetting
    | HomeDeliverySetting
    | EnabledShippingTypesSetting
    | PaymentMehodSetting
    | CashOnDeliverySetting
    | TourmixApiKeysSetting
    | RatesToPaymentMethodsSetting
    | SenderDetailsExtraMplSetting
    | MappedPaymentMethods
    | ShippingMethodsSetting;
